import { useState } from 'react';
import {
  makeStyles, tokens, Title2, Title3, Body1, Caption1, Spinner, Button,
  MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { ProjectPicker } from '../../../components/ProjectPicker';
import { useSignoffQueue } from '../../../api/hooks';
import type { SignoffItem } from '../../../api/types';

// FEAT-028 Sign-off Queue (read-only). Adobe Sign + Teams approval mechanisms are
// stubbed pending the OpsMgr-side integration; the buttons render but are disabled.
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  toolbar: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  list: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  card: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  header: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, paddingTop: tokens.spacingVerticalS, borderTop: `1px solid ${tokens.colorNeutralStroke2}` },
});

export function SignoffQueuePage() {
  const styles = useStyles();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { data, isLoading, error } = useSignoffQueue(projectId);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Title2>Sign Off Completed Work</Title2>
        <Body1 className={styles.hint}>
          Initiatives that have completed UAT and are waiting on your sign-off. One-click Sign via Adobe / Sign in Teams is wired separately — these buttons will activate once that integration is live.
        </Body1>
        <ProjectPicker selectedProjectId={projectId} onSelect={setProjectId} />
      </div>

      {!projectId ? (
        <Body1>Pick a project to see what's waiting on your sign-off.</Body1>
      ) : isLoading ? (
        <Spinner label="Loading…" />
      ) : error ? (
        <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>
      ) : !data || data.items.length === 0 ? (
        <Body1>Nothing is waiting on your sign-off. 🎉</Body1>
      ) : (
        <div className={styles.list}>
          {data.items.map((item: SignoffItem, idx: number) => (
            <div key={idx} className={styles.card}>
              <div className={styles.header}>
                <Title3>{item.customerTitle}</Title3>
                <Caption1>Waiting {item.daysAwaiting} day{item.daysAwaiting === 1 ? '' : 's'} since {new Date(item.awaitingSince).toLocaleDateString()}</Caption1>
              </div>
              {item.customerDescription ? <Body1>{item.customerDescription}</Body1> : null}
              <div className={styles.actions}>
                <Button
                  appearance="primary"
                  disabled={!item.adobeSignUrl}
                  as={item.adobeSignUrl ? 'a' : 'button'}
                  href={item.adobeSignUrl ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Sign ${item.customerTitle} via Adobe Sign`}
                >
                  Sign via Adobe
                </Button>
                <Button
                  appearance="secondary"
                  disabled={!item.teamsChatUrl}
                  as={item.teamsChatUrl ? 'a' : 'button'}
                  href={item.teamsChatUrl ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Sign ${item.customerTitle} in Teams`}
                >
                  Sign in Teams
                </Button>
                {item.signoffPackUrl && (
                  <Button
                    appearance="subtle"
                    as="a"
                    href={item.signoffPackUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Sign-off Pack
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
